import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Header from '@/components/layout/Header';
import { showSuccess, showError } from '@/lib/swal';
import { RSVP, Event, UnitType } from '@/types';
import { User } from 'lucide-react';
import { useRanks } from '@/hooks/useRanks';
import { getAvailablePrefixes, getRankAndShortName } from '@/lib/rank-utils';

export default function EditRSVPDetailsPage() {
  const { unitId: unitId, eventId, rsvpId } = useParams<{ unitId: string; eventId: string; rsvpId: string }>();
  const { currentUser } = useAuth();
  const { ranks } = useRanks();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [rsvp, setRsvp] = useState<RSVP | null>(null);
  const [event, setEvent] = useState<Event | null>(null);
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  const [currentUnit, setCurrentUnit] = useState<any>(null);

  // Form state - only personal details
  const [name, setName] = useState('');
  const [rank, setRank] = useState('');
  const [additionalRank, setAdditionalRank] = useState('');
  const [unitName, setUnitName] = useState('');
  const [email, setEmail] = useState('');

  const currentUnitType = currentUnit ? unitTypes.find((ut) => ut.id === currentUnit.typeId) : undefined;
  const unitLabel = currentUnitType?.entityName || 'Unit/Organization';
  const requireAffiliationInfo = event?.requirePrefixRankUnitInfo ?? true;

  const availablePrefixes = useMemo(
    () => getAvailablePrefixes(event, currentUnitType),
    [event, currentUnitType],
  );

  // Fetch unit types
  useEffect(() => {
    const fetchUnitTypes = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
        if (settingsDoc.exists()) {
          const data = settingsDoc.data();
          setUnitTypes(data.unitTypes || []);
        }
      } catch (error) {
        console.error('Error fetching unit types:', error);
      }
    };

    fetchUnitTypes();
  }, []);

  // Fetch current unit
  useEffect(() => {
    const fetchCurrentUnit = async () => {
      if (!unitId) return;
      
      try {
        const unitDoc = await getDoc(doc(db, 'units', unitId));
        if (unitDoc.exists()) {
          setCurrentUnit({
            id: unitDoc.id,
            ...unitDoc.data()
          });
        }
      } catch (error) {
        console.error('Error fetching current unit:', error);
      }
    };

    fetchCurrentUnit();
  }, [unitId]);

  useEffect(() => {
    const fetchData = async () => {
      if (!unitId || !eventId || !rsvpId) {
        showError('Invalid RSVP link');
        setLoading(false);
        return;
      }

      try {
        // Fetch RSVP
        const rsvpDoc = await getDoc(doc(db, 'units', unitId, 'events', eventId, 'rsvps', rsvpId));
        if (!rsvpDoc.exists()) {
          showError('RSVP not found');
          setLoading(false);
          return;
        }

        const rsvpData = rsvpDoc.data() as RSVP;
        
        // Check if user is authorized
        if (rsvpData.userId !== currentUser?.uid) {
          showError('You are not authorized to edit this RSVP');
          navigate('/dashboard');
          return;
        }

        // Fetch event
        const eventDoc = await getDoc(doc(db, 'units', unitId, 'events', eventId));
        if (!eventDoc.exists()) {
          showError('Event not found');
          setLoading(false);
          return;
        }

        const eventData = eventDoc.data();
        const eventDate = eventData.date?.toDate() || new Date();
        const eventObj: Event = {
          id: eventDoc.id,
          unitId,
          name: eventData.name,
          date: eventDate,
          location: eventData.location,
          costPerPerson: eventData.costPerPerson,
          questionnaire: eventData.questionnaire || [],
          courses: eventData.courses || [],
          tickets: eventData.tickets || [],
          maxCapacity: eventData.maxCapacity,
          currentAttendees: eventData.currentAttendees || 0,
          waitlistEnabled: eventData.waitlistEnabled || false,
          waitlist: eventData.waitlist || [],
          rsvpDeadline: eventData.rsvpDeadline?.toDate() || new Date(),
          paymentDeadline: eventData.paymentDeadline?.toDate() || eventDate,
          eventStatus: eventData.eventStatus,
          dresscode: eventData.dresscode,
          customInstructions: eventData.customInstructions,
          isPublic: eventData.isPublic ?? false,
          requirePrefixRankUnitInfo: eventData.requirePrefixRankUnitInfo ?? true,
          useCustomPrefixes: Boolean(eventData.useCustomPrefixes),
          customPrefixes: Array.isArray(eventData.customPrefixes)
            ? eventData.customPrefixes.filter((p: unknown) => typeof p === 'string' && p.trim() !== '')
            : [],
          createdBy: eventData.createdBy,
          createdAt: eventData.createdAt?.toDate() || new Date(),
        };

        // Check if past deadline
        if (new Date() > eventObj.rsvpDeadline) {
          showError('The RSVP deadline has passed. You can no longer edit your registration.');
          setLoading(false);
          return;
        }

        setEvent(eventObj);
        setRsvp(rsvpData);
        
        // Populate form with personal details only
        setName(rsvpData.name);
        setRank(rsvpData.rank);
        setAdditionalRank(rsvpData.additionalRank || '');
        setUnitName(rsvpData.unit);
        setEmail(rsvpData.email);

      } catch (error) {
        console.error('Error fetching data:', error);
        showError('Failed to load RSVP');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [unitId, eventId, rsvpId, currentUser, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedRank = rank?.trim() || '';
    const trimmedUnitName = unitName?.trim() || '';
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const normalizedAdditionalRank = additionalRank?.trim() || '';

    if (requireAffiliationInfo) {
      if (!trimmedRank) {
        showError('Please select your prefix.');
        return;
      }
    }

    if (!trimmedName) {
      showError('Please fill in all required fields');
      return;
    }

    if (!unitId || !eventId || !rsvpId) {
      showError('Invalid RSVP');
      return;
    }

    setSubmitting(true);
    try {
      const updateData: any = {
        name: trimmedName,
        email: trimmedEmail,
        updatedAt: new Date(),
      };

      if (requireAffiliationInfo) {
        const { rank: resolvedRank, rankShortName } = getRankAndShortName({
          rankName: trimmedRank,
          currentUnitType,
          globalRanks: ranks,
        });

        updateData.rank = resolvedRank;
        updateData.rankShortName = rankShortName ?? null;
        updateData.unit = trimmedUnitName || null;
        // Always persist additionalRank explicitly so it can be cleared
        updateData.additionalRank = normalizedAdditionalRank || null;
      }

      await updateDoc(doc(db, 'units', unitId, 'events', eventId, 'rsvps', rsvpId), updateData);

      showSuccess('Personal details updated successfully!');
      navigate(`/unit/${unitId}/event/${eventId}/rsvp/${rsvpId}/dashboard`);
    } catch (error) {
      console.error('Error updating RSVP:', error);
      showError('Failed to update RSVP. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading RSVP...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!event || !rsvp) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <Card className="max-w-md">
            <CardHeader>
              <CardTitle>RSVP Not Found</CardTitle>
              <CardDescription>The RSVP you're looking for doesn't exist or you don't have permission to edit it.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate('/dashboard')}>Return to Dashboard</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 px-4 py-8">
        <div className="container mx-auto max-w-3xl">
          <div className="mb-8">
            <Button variant="ghost" onClick={() => navigate(`/unit/${unitId}/event/${eventId}/rsvp/${rsvpId}/dashboard`)}>
              ← Back to RSVP Dashboard
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Edit Personal Details
              </CardTitle>
              <CardDescription>
                Update your personal information for {event.name}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Details */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Personal Details</h3>
                  
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="John Smith"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>

                  {requireAffiliationInfo && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="rank">Prefix *</Label>
                        <Select value={rank} onValueChange={setRank}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select your prefix" />
                          </SelectTrigger>
                          <SelectContent>
                            {availablePrefixes.map((prefix) => (
                              <SelectItem key={prefix.id} value={prefix.name}>
                                {prefix.shortName ? `${prefix.name} (${prefix.shortName})` : prefix.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="additionalRank">Rank</Label>
                        <Input
                          id="additionalRank"
                          type="text"
                          placeholder="eg. ProvJGW / PPADoC / AGSec"
                          value={additionalRank}
                          onChange={(e) => setAdditionalRank(e.target.value)}
                          required={requireAffiliationInfo}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="unit">
                          {unitLabel}{' '}
                          <span className="font-normal text-muted-foreground">(optional)</span>
                        </Label>
                        <Input
                          id="unit"
                          type="text"
                          placeholder={unitLabel}
                          value={unitName}
                          onChange={(e) => setUnitName(e.target.value)}
                        />
                      </div>
                    </>
                  )}
                </div>

                {/* Submit Button */}
                <div className="flex justify-end space-x-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => navigate(`/unit/${unitId}/event/${eventId}/rsvp/${rsvpId}/dashboard`)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? 'Updating...' : 'Update Details'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}

